import { ref } from 'vue'

interface ToastOptions {
  message: string
  type?: 'success' | 'error' | 'warning' | 'info'
  duration?: number
  position?:
    | 'top'
    | 'bottom'
    | 'top right'
    | 'top left'
    | 'bottom right'
    | 'bottom left'
  dismissible?: boolean
  showProgress?: boolean
  variant?: 'elevated' | 'flat' | 'tonal' | 'outlined' | 'text'
  icon?: string | false // false to disable icon, string for custom icon
}

interface Toast extends ToastOptions {
  id: number
  show: boolean
}

// Default icons for each toast type
const DEFAULT_ICONS: Record<string, string> = {
  success: 'mdi-check-circle',
  error: 'mdi-alert-circle',
  warning: 'mdi-alert',
  info: 'mdi-information',
}

// Default variants for each toast type
const DEFAULT_VARIANTS: Record<
  string,
  'elevated' | 'flat' | 'tonal' | 'outlined' | 'text'
> = {
  success: 'flat',
  error: 'flat',
  warning: 'flat',
  info: 'flat',
}

const toasts = ref<Toast[]>([])
let toastId = 0

// Maximum number of toasts to show at once
const MAX_TOASTS = 3

export function useToast() {
  const showToast = (options: ToastOptions) => {
    const id = toastId++

    // Remove oldest visible toast if at limit (FIFO)
    const visibleToasts = toasts.value.filter(t => t.show)
    if (visibleToasts.length >= MAX_TOASTS) {
      const oldestId = visibleToasts[0].id
      dismissToast(oldestId)
    }

    // Determine the type first to set defaults
    const toastType = options.type || 'info'

    const toast: Toast = {
      id,
      show: true,
      type: 'info',
      duration: 3000,
      position: 'top',
      dismissible: true,
      showProgress: true,
      variant: DEFAULT_VARIANTS[toastType] || 'flat',
      icon: DEFAULT_ICONS[toastType],
      ...options,
    }

    toasts.value.push(toast)

    // Auto-dismiss
    if (toast.duration && toast.duration > 0) {
      setTimeout(() => {
        dismissToast(id)
      }, toast.duration)
    }

    return id
  }

  const dismissToast = (id: number) => {
    const index = toasts.value.findIndex(t => t.id === id)
    if (index !== -1) {
      toasts.value[index].show = false
      // Remove from array after transition (300ms)
      setTimeout(() => {
        toasts.value = toasts.value.filter(t => t.id !== id)
      }, 300)
    }
  }

  const dismissAll = () => {
    toasts.value.forEach(toast => {
      toast.show = false
    })
    setTimeout(() => {
      toasts.value = []
    }, 300)
  }

  const clearAll = () => {
    // Immediate clear for testing
    toasts.value = []
  }

  const success = (message: string, duration = 3000) => {
    return showToast({ message, type: 'success', duration })
  }

  const error = (message: string, duration = 5000) => {
    return showToast({ message, type: 'error', duration })
  }

  const warning = (message: string, duration = 4000) => {
    return showToast({ message, type: 'warning', duration })
  }

  const info = (message: string, duration = 3000) => {
    return showToast({ message, type: 'info', duration })
  }

  return {
    toasts,
    showToast,
    dismissToast,
    dismissAll,
    clearAll,
    success,
    error,
    warning,
    info,
  }
}
